import { EFileStatus } from '@constants';
import { IBasicFile, IFile } from '@sdk/contracts';
import {
  MediaLoadingStrategy,
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Poster,
  VideoMimeType
} from '@vidstack/react';
import { PlyrLayout, plyrLayoutIcons } from '@vidstack/react/player/layouts/plyr';
import { Alert } from 'antd';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import VIDEO_POSTER from '@/assets/video_poster.png';
import { useProfile } from '@/store';

import './styles.css';
import '@vidstack/react/player/styles/base.css';
import '@vidstack/react/player/styles/plyr/theme.css';

interface Props {
  file?: IFile | IBasicFile;
  loadStrategy?: MediaLoadingStrategy;
  onPlay?: () => void;
  onPause?: () => void;
  onReady?: () => void;
  onSeeked?: (time: number) => void;
  onProgress?: (progress: number) => void;
}

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const VideoPlayer = forwardRef<MediaPlayerInstance, Props>(
  ({ file, loadStrategy, onPlay, onPause, onReady, onProgress, onSeeked }, ref) => {
    const { t } = useTranslation();
    const {
      roleCheck: { isManager, isAdmin }
    } = useProfile();
    const canDownload = isManager || isAdmin;

    if (!file || !file.url || !isValidUrl(file.url))
      return <Alert type="error" showIcon message={t('CANT_LOAD_VIDEO_FILE')} />;

    return (
      <MediaPlayer
        ref={ref}
        src={
          file.status === EFileStatus.READY
            ? file.url
            : { src: file.url, type: file.mimetype as VideoMimeType }
        }
        load={loadStrategy}
        onPlay={onPlay}
        onPause={onPause}
        onCanPlay={onReady}
        onTimeUpdate={e => onProgress?.(e.currentTime)}
        onSeeked={time => onSeeked?.(time)}
        hideControlsOnMouseLeave>
        <MediaProvider />
        <PlyrLayout
          icons={plyrLayoutIcons}
          controls={[
            'play',
            'progress',
            'current-time',
            'mute+volume',
            'settings',
            canDownload ? 'download' : 'pip',
            'fullscreen'
          ]}
          download={file.url}
          slots={
            canDownload
              ? {
                  download: (
                    <a
                      className="plyr__controls__item plyr__control"
                      href={file.url}
                      download
                      target="_blank"
                      rel="noreferrer">
                      <plyrLayoutIcons.Download />
                      <span className="plyr__tooltip">{t('DOWNLOAD')}</span>
                    </a>
                  )
                }
              : undefined
          }
        />
        {loadStrategy && (
          <Poster className="media-poster" src={VIDEO_POSTER} alt="Poster placeholder" />
        )}
      </MediaPlayer>
    );
  }
);
